import { useState, useEffect } from "react";
import axios from "axios";
import { Shield, Save, Trash2, Plus, LogOut, Database, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/meals`;
const STORAGE_KEY = "dta-admin-token";

const EMPTY_MEAL = {
  provider_slug: "frive",
  meal_id: "",
  name: "",
  image_url: "",
  meal_type: "lunch",
  calories: 500,
  protein: 40,
  carbs: 50,
  fat: 15,
  fibre: 5,
  dietary_tags: "",
  allergens: "",
  product_url: "",
  availability: "available",
};

export default function AdminMealsPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [passInput, setPassInput] = useState("");
  const [loginError, setLoginError] = useState("");
  const [provider, setProvider] = useState(null);
  const [meals, setMeals] = useState([]);
  const [status, setStatus] = useState("");
  const [newMeal, setNewMeal] = useState(EMPTY_MEAL);

  useEffect(() => {
    if (!token) return;
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function loadAll() {
    try {
      const [p, m] = await Promise.all([
        axios.get(`${API}/providers/frive`),
        axios.get(`${API}/catalogue/frive`),
      ]);
      setProvider(p.data);
      setMeals(m.data);
    } catch (e) {
      setStatus("Failed to load data.");
    }
  }

  async function logIn(e) {
    e.preventDefault();
    setLoginError("");
    try {
      // ping an admin-only endpoint using the password as the token
      await axios.put(
        `${API}/admin/providers/frive`,
        { name: undefined },
        { headers: { "X-Admin-Token": passInput } },
      );
      // On 400 "No changes supplied" we still auth'd successfully — treat that as OK
      sessionStorage.setItem(STORAGE_KEY, passInput);
      setToken(passInput);
    } catch (err) {
      if (err.response?.status === 400) {
        // auth passed, request body was empty — accept
        sessionStorage.setItem(STORAGE_KEY, passInput);
        setToken(passInput);
      } else {
        setLoginError("Wrong password. Try again.");
      }
    }
  }

  function logOut() {
    sessionStorage.removeItem(STORAGE_KEY);
    setToken("");
    setPassInput("");
    setProvider(null);
    setMeals([]);
  }

  async function saveProvider() {
    try {
      const payload = { ...provider };
      delete payload.id;
      delete payload.updated_at;
      delete payload.slug;
      const res = await axios.put(`${API}/admin/providers/frive`, payload, {
        headers: { "X-Admin-Token": token },
      });
      setProvider(res.data);
      setStatus("Provider saved.");
    } catch (e) {
      setStatus(`Save failed: ${e.response?.data?.detail || e.message}`);
    }
  }

  async function createMeal() {
    try {
      const payload = {
        ...newMeal,
        calories: Number(newMeal.calories),
        protein: Number(newMeal.protein),
        carbs: Number(newMeal.carbs),
        fat: Number(newMeal.fat),
        fibre: newMeal.fibre === "" ? null : Number(newMeal.fibre),
        dietary_tags: splitCsv(newMeal.dietary_tags),
        allergens: splitCsv(newMeal.allergens),
      };
      await axios.post(`${API}/admin/meals`, payload, {
        headers: { "X-Admin-Token": token },
      });
      setStatus(`Meal added: ${payload.name}`);
      setNewMeal(EMPTY_MEAL);
      loadAll();
    } catch (e) {
      setStatus(`Create failed: ${e.response?.data?.detail || e.message}`);
    }
  }

  async function deleteMeal(id, name) {
    if (!window.confirm(`Delete meal "${name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/meals/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      setStatus(`Deleted: ${name}`);
      loadAll();
    } catch (e) {
      setStatus(`Delete failed: ${e.response?.data?.detail || e.message}`);
    }
  }

  async function reseed() {
    if (!window.confirm("Wipe and reseed the Frive sample catalogue?")) return;
    try {
      await axios.post(`${API}/admin/seed`, {}, { headers: { "X-Admin-Token": token } });
      setStatus("Reseeded.");
      loadAll();
    } catch (e) {
      setStatus(`Reseed failed: ${e.response?.data?.detail || e.message}`);
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
        <form
          onSubmit={logIn}
          className="w-full max-w-sm bg-[#031D47] border border-white/15 p-8"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5" strokeWidth={1.75} />
            <p className="dta-overline text-white/60">DTA admin</p>
          </div>
          <h1 className="font-display uppercase font-bold text-2xl md:text-3xl mt-3">
            Meal provider.
          </h1>
          <p className="mt-3 text-sm text-white/70">
            Enter the admin password to edit the Frive provider and sample catalogue.
          </p>
          <input
            type="password"
            value={passInput}
            onChange={(e) => setPassInput(e.target.value)}
            placeholder="Admin password"
            className="mt-6 w-full py-3 px-3 bg-white/10 border border-white/15 text-white placeholder-white/40"
            data-testid="admin-password-input"
            autoFocus
          />
          {loginError && (
            <p className="mt-3 text-sm text-red-300 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" strokeWidth={1.75} /> {loginError}
            </p>
          )}
          <button
            type="submit"
            className="mt-5 dta-btn dta-btn-primary w-full"
            data-testid="admin-login-submit"
          >
            Sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F3EF] py-10">
      <div className="dta-container max-w-5xl">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="dta-overline text-navy">DTA admin</p>
            <h1 className="font-display uppercase font-bold text-3xl md:text-4xl mt-2">
              Meal provider.
            </h1>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={reseed}
              className="dta-btn dta-btn-outline-dark"
              data-testid="admin-reseed"
            >
              <Database className="w-4 h-4" strokeWidth={1.75} /> Reseed
            </button>
            <button
              type="button"
              onClick={logOut}
              className="dta-btn dta-btn-outline-dark"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.75} /> Sign out
            </button>
          </div>
        </div>

        {status && (
          <p className="mt-4 text-sm bg-white border border-black/10 px-4 py-2.5">{status}</p>
        )}

        {/* Provider editor */}
        {provider && (
          <section className="mt-8 bg-white border border-black/10 p-6 md:p-8" data-testid="admin-provider-section">
            <p className="dta-overline text-navy">Provider configuration</p>
            <h2 className="font-display uppercase font-bold text-2xl mt-2">
              {provider.name}
            </h2>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Name">
                <input
                  type="text"
                  value={provider.name || ""}
                  onChange={(e) => setProvider({ ...provider, name: e.target.value })}
                  className="w-full py-2.5 px-3 border border-black/20"
                  data-testid="admin-provider-name"
                />
              </Field>
              <Field label="Logo URL">
                <input
                  type="text"
                  value={provider.logo_url || ""}
                  onChange={(e) => setProvider({ ...provider, logo_url: e.target.value })}
                  className="w-full py-2.5 px-3 border border-black/20"
                />
              </Field>
              <Field label="Outbound URL">
                <input
                  type="text"
                  value={provider.outbound_url || ""}
                  onChange={(e) => setProvider({ ...provider, outbound_url: e.target.value })}
                  className="w-full py-2.5 px-3 border border-black/20"
                  data-testid="admin-provider-url"
                />
              </Field>
              <Field label="Affiliate code">
                <input
                  type="text"
                  value={provider.affiliate_code || ""}
                  onChange={(e) => setProvider({ ...provider, affiliate_code: e.target.value })}
                  className="w-full py-2.5 px-3 border border-black/20"
                />
              </Field>
              <Field label="Partnership status">
                <select
                  value={provider.partnership_status || "independent"}
                  onChange={(e) => setProvider({ ...provider, partnership_status: e.target.value })}
                  className="w-full py-2.5 px-3 border border-black/20 bg-white"
                >
                  <option value="independent">independent</option>
                  <option value="affiliate">affiliate</option>
                  <option value="official">official partner</option>
                </select>
              </Field>
              <Field label="Active?">
                <label className="inline-flex items-center gap-2 py-2.5">
                  <input
                    type="checkbox"
                    checked={!!provider.is_active}
                    onChange={(e) => setProvider({ ...provider, is_active: e.target.checked })}
                    className="w-5 h-5 accent-[#031D47]"
                  />
                  <span className="text-sm">Show on the public macros page</span>
                </label>
              </Field>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-5">
              <Field label="Long disclaimer (use {provider_name} as placeholder)">
                <textarea
                  rows={5}
                  value={provider.disclaimer_long || ""}
                  onChange={(e) => setProvider({ ...provider, disclaimer_long: e.target.value })}
                  className="w-full py-2.5 px-3 border border-black/20"
                  data-testid="admin-disclaimer-long"
                />
              </Field>
              <Field label="Short disclaimer (used under meal cards + outbound block)">
                <textarea
                  rows={3}
                  value={provider.disclaimer_short || ""}
                  onChange={(e) => setProvider({ ...provider, disclaimer_short: e.target.value })}
                  className="w-full py-2.5 px-3 border border-black/20"
                />
              </Field>
            </div>

            <button
              type="button"
              onClick={saveProvider}
              className="dta-btn dta-btn-dark mt-6"
              data-testid="admin-provider-save"
            >
              <Save className="w-4 h-4" strokeWidth={1.75} /> Save provider
            </button>
          </section>
        )}

        {/* Add meal */}
        <section className="mt-8 bg-white border border-black/10 p-6 md:p-8" data-testid="admin-add-meal">
          <p className="dta-overline text-navy">Add meal</p>
          <h2 className="font-display uppercase font-bold text-2xl mt-2">New meal.</h2>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Meal ID (slug)">
              <input
                type="text"
                value={newMeal.meal_id}
                onChange={(e) => setNewMeal({ ...newMeal, meal_id: e.target.value })}
                className="w-full py-2.5 px-3 border border-black/20"
              />
            </Field>
            <Field label="Name">
              <input
                type="text"
                value={newMeal.name}
                onChange={(e) => setNewMeal({ ...newMeal, name: e.target.value })}
                className="w-full py-2.5 px-3 border border-black/20"
              />
            </Field>
            <Field label="Meal type">
              <select
                value={newMeal.meal_type}
                onChange={(e) => setNewMeal({ ...newMeal, meal_type: e.target.value })}
                className="w-full py-2.5 px-3 border border-black/20 bg-white"
              >
                <option value="breakfast">breakfast</option>
                <option value="lunch">lunch</option>
                <option value="dinner">dinner</option>
              </select>
            </Field>

            <Field label="Calories">
              <input
                type="number"
                value={newMeal.calories}
                onChange={(e) => setNewMeal({ ...newMeal, calories: e.target.value })}
                className="w-full py-2.5 px-3 border border-black/20"
              />
            </Field>
            <Field label="Protein (g)">
              <input
                type="number"
                value={newMeal.protein}
                onChange={(e) => setNewMeal({ ...newMeal, protein: e.target.value })}
                className="w-full py-2.5 px-3 border border-black/20"
              />
            </Field>
            <Field label="Carbs (g)">
              <input
                type="number"
                value={newMeal.carbs}
                onChange={(e) => setNewMeal({ ...newMeal, carbs: e.target.value })}
                className="w-full py-2.5 px-3 border border-black/20"
              />
            </Field>
            <Field label="Fat (g)">
              <input
                type="number"
                value={newMeal.fat}
                onChange={(e) => setNewMeal({ ...newMeal, fat: e.target.value })}
                className="w-full py-2.5 px-3 border border-black/20"
              />
            </Field>
            <Field label="Fibre (g, optional)">
              <input
                type="number"
                value={newMeal.fibre}
                onChange={(e) => setNewMeal({ ...newMeal, fibre: e.target.value })}
                className="w-full py-2.5 px-3 border border-black/20"
              />
            </Field>
            <Field label="Image URL">
              <input
                type="text"
                value={newMeal.image_url}
                onChange={(e) => setNewMeal({ ...newMeal, image_url: e.target.value })}
                className="w-full py-2.5 px-3 border border-black/20"
              />
            </Field>
            <Field label="Dietary tags (comma separated)">
              <input
                type="text"
                value={newMeal.dietary_tags}
                onChange={(e) => setNewMeal({ ...newMeal, dietary_tags: e.target.value })}
                placeholder="high-protein, gluten-free"
                className="w-full py-2.5 px-3 border border-black/20"
              />
            </Field>
            <Field label="Allergens (comma separated)">
              <input
                type="text"
                value={newMeal.allergens}
                onChange={(e) => setNewMeal({ ...newMeal, allergens: e.target.value })}
                placeholder="gluten, egg, milk"
                className="w-full py-2.5 px-3 border border-black/20"
              />
            </Field>
            <Field label="Product URL">
              <input
                type="text"
                value={newMeal.product_url}
                onChange={(e) => setNewMeal({ ...newMeal, product_url: e.target.value })}
                className="w-full py-2.5 px-3 border border-black/20"
              />
            </Field>
          </div>

          <button
            type="button"
            onClick={createMeal}
            className="dta-btn dta-btn-dark mt-6"
            data-testid="admin-meal-create"
          >
            <Plus className="w-4 h-4" strokeWidth={1.75} /> Add meal
          </button>
        </section>

        {/* Meals list */}
        <section className="mt-8 bg-white border border-black/10 p-6 md:p-8" data-testid="admin-meals-list">
          <p className="dta-overline text-navy">Catalogue</p>
          <h2 className="font-display uppercase font-bold text-2xl mt-2">
            {meals.length} meal{meals.length === 1 ? "" : "s"}.
          </h2>

          <ul className="mt-6 divide-y divide-black/10">
            {meals.map((m) => (
              <li
                key={m.id}
                className="py-3 flex items-center justify-between gap-4"
                data-testid={`admin-meal-row-${m.meal_id}`}
              >
                <div className="min-w-0">
                  <p className="font-medium text-[#0A0A0A] truncate">{m.name}</p>
                  <p className="dta-overline text-[0.55rem] text-[#272727]/60 mt-0.5">
                    {m.meal_type} · {m.calories} kcal · P{m.protein} C{m.carbs} F{m.fat}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => deleteMeal(m.id, m.name)}
                  className="dta-overline text-[0.65rem] text-red-700 hover:opacity-70 inline-flex items-center gap-1.5 shrink-0"
                  data-testid={`admin-meal-delete-${m.meal_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} /> Delete
                </button>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="dta-overline text-[0.65rem] text-[#272727] block mb-2">{label}</span>
      {children}
    </label>
  );
}

function splitCsv(s) {
  return (s || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}
